import './style.css';
import { applyTheme, loadTheme } from './theme.js';
import { loadConfig, isConfigured, loadOperatorSession } from './config.js';
import { renderSettings } from './screens/settings.js';
import { renderLogin } from './screens/login.js';
import { renderHome } from './screens/home.js';
import { renderAsnList } from './screens/asnList.js';
import { renderScan } from './screens/scan.js';
import { renderReview } from './screens/review.js';
import { renderConfirmed } from './screens/confirmed.js';
import { renderStockCount } from './screens/stockCount.js';
import { renderSkuCount } from './screens/skuCount.js';
import { renderStockLookup } from './screens/stockLookup.js';
import { renderPickList, renderPickWork } from './screens/pick.js';
import { renderReplen } from './screens/replen.js';
import { renderRelease } from './screens/release.js';
import { escapeHtml } from './dom.js';

const app = document.getElementById('app');
if (!app) throw new Error('#app root element missing from index.html');

function parseHash(): { name: string; params: string[] } {
  const hash = location.hash.replace(/^#\/?/, '');
  const parts = hash.split('/').filter(Boolean);
  return { name: parts[0] ?? '', params: parts.slice(1) };
}

applyTheme(loadTheme());

async function route(): Promise<void> {
  const { name, params } = parseHash();
  const config = loadConfig();

  // Device-level setup (Worker URL + app token) comes first — without it
  // no request (including the Login screen's own operator list) can
  // reach the server at all.
  if (name !== 'settings' && !isConfigured(config)) {
    if (location.hash !== '#/settings') {
      location.hash = '#/settings';
      return;
    }
  }

  // Real per-operator login (migrations/0005_operator_login.sql) — every
  // screen except Settings and Login itself requires a live operator
  // session, replacing the old free-text Operator ID that let anyone act
  // as anyone.
  if (isConfigured(config) && name !== 'settings' && name !== 'login' && !loadOperatorSession()) {
    if (location.hash !== '#/login') {
      location.hash = '#/login';
      return;
    }
  }

  app!.innerHTML = '';
  document.body.dataset.job = JOB_BY_ROUTE[name] ?? '';
  try {
    switch (name) {
      case '':
        await renderHome(app!);
        break;
      case 'asns':
        await renderAsnList(app!);
        break;
      case 'settings':
        await renderSettings(app!);
        break;
      case 'login':
        await renderLogin(app!);
        break;
      case 'scan':
        if (!params[0]) return void (location.hash = '#/asns');
        await renderScan(app!, params[0]);
        break;
      case 'review':
        if (!params[0]) return void (location.hash = '#/asns');
        await renderReview(app!, params[0]);
        break;
      case 'confirmed':
        if (!params[0]) return void (location.hash = '#/asns');
        await renderConfirmed(app!, params[0]);
        break;
      case 'stock-count':
        await renderStockCount(app!);
        break;
      case 'sku-count':
        await renderSkuCount(app!);
        break;
      case 'stock-lookup':
        await renderStockLookup(app!);
        break;
      case 'replen':
        await renderReplen(app!);
        break;
      case 'release':
        await renderRelease(app!);
        break;
      case 'pick':
        if (params[0] && params[1]) await renderPickWork(app!, decodeURIComponent(params[0]), decodeURIComponent(params[1]));
        else await renderPickList(app!);
        break;
      default:
        location.hash = '#/';
    }
  } catch (err) {
    renderFatalError(err);
  }
}

/** Which job a route belongs to: drives the coloured strip under the page title (see style.css, body[data-job]). */
const JOB_BY_ROUTE: Record<string, string> = {
  asns: 'receiving',
  scan: 'receiving',
  review: 'receiving',
  confirmed: 'receiving',
  pick: 'picking',
  replen: 'replen',
  release: 'picking',
  'stock-lookup': 'stockCheck',
  'stock-count': 'stockCount',
  'sku-count': 'stockCount',
  settings: 'settings',
};

function renderFatalError(err: unknown): void {
  app!.innerHTML = '';
  const div = document.createElement('div');
  div.className = 'screen';
  const message = err instanceof Error ? err.message : String(err);
  div.innerHTML = `<div class="card"><p class="error-text">Something went wrong.</p><p class="hint">${escapeHtml(message)}</p></div><button class="primary" id="retry">Retry</button>`;
  app!.appendChild(div);
  div.querySelector<HTMLButtonElement>('#retry')?.addEventListener('click', () => void route());
}

window.addEventListener('hashchange', () => void route());
// Dispatched by api.ts the moment any request comes back 401 — the
// operator session was already cleared locally by then, so this just
// moves the operator to Login immediately rather than waiting for them
// to notice their next action silently failed.
window.addEventListener('wr:session-expired', () => {
  if (location.hash !== '#/login') {
    location.hash = '#/login';
  } else {
    void route();
  }
});
window.addEventListener('DOMContentLoaded', () => {
  void route();
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  }
});
